import { Snackbar as MuiSnackbar, Alert, Box, IconButton } from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import Warning from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import Close from '@mui/icons-material/Close'
import { CleanThemeProvider } from 'shared/theme/cleanThemeProvider'

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info'

export interface SnackbarAction {
  label: string
  onClick: () => void
  variant?: 'text' | 'primary' | 'dark' | 'contained'
}

export interface SnackbarProps {
  open: boolean
  onClose: () => void
  severity?: SnackbarSeverity
  title: string
  message?: string
  actions?: SnackbarAction[]
  autoHideDuration?: number | null
  /**
   * 表示位置。管理画面の通知は notistack provider と揃えて既定の top/right に
   * 統一する方針なので、原則として指定しない。viewer の全画面ビューア内など
   * 下中央に出したい特殊な文脈（例: ViewerInviteDialog）に限り bottom/center 等で
   * 上書きする。
   * @default { vertical: 'top', horizontal: 'right' }
   */
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
}

const severityConfig = {
  success: {
    main: '#25C2B6',
    light: '#69d1c9',
    dark: '#069388',
    lighter: '#D3FCD2', // 背景色 (Figmaデザインより)
    darker: '#065E49', // テキスト色 (Figmaデザインより)
    icon: CheckCircle
  },
  error: {
    main: '#E62E2E',
    light: '#fa7373',
    dark: '#ad1d1d',
    lighter: '#FFE5E5', // 推測値（要確認）
    darker: '#8B0000', // 推測値（要確認）
    icon: ErrorIcon
  },
  warning: {
    main: '#F38D07',
    light: '#f5b35d',
    dark: '#bf6d02',
    lighter: '#FFF4E5', // 推測値（要確認）
    darker: '#9A5A00', // 推測値（要確認）
    icon: Warning
  },
  info: {
    main: '#42A6DB',
    light: '#73bde5',
    dark: '#3e7da0',
    lighter: '#E3F2FD', // 推測値（要確認）
    darker: '#1565C0', // 推測値（要確認）
    icon: InfoIcon
  }
}

const getPrimaryActionStyles = (mainColor: string) => ({
  bgcolor: mainColor,
  '&:hover': {
    opacity: 0.9
  }
})

const getSecondaryActionStyles = (mainColor: string) => ({
  border: `1px solid ${mainColor}80`,
  '&:hover': {
    bgcolor: 'rgba(0, 0, 0, 0.04)'
  }
})

function SnackbarContent ({
  open,
  onClose,
  severity = 'success',
  title,
  message,
  actions,
  autoHideDuration,
  anchorOrigin = { vertical: 'top', horizontal: 'right' }
}: SnackbarProps) {
  const config = severityConfig[severity]
  const IconComponent = config.icon

  // errorの場合は自動で閉じない、それ以外は6秒後に閉じる
  const duration = autoHideDuration !== undefined
    ? autoHideDuration
    : severity === 'error'
      ? null
      : 6000

  return (
    <MuiSnackbar
      anchorOrigin={anchorOrigin}
      autoHideDuration={duration}
      onClose={onClose}
      open={open}
    >
      <Alert
        icon={false}
        sx={{
          bgcolor: config.lighter,
          color: config.darker,
          borderRadius: '8px',
          boxShadow: '0px 4px 8px 0px rgba(26, 26, 26, 0.25)',
          padding: '6px 8px 6px 0',
          minWidth: '400px',
          alignItems: 'flex-start',
          '& .MuiAlert-message': {
            padding: 0,
            width: '100%'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          {/* Icon Container */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pl: 2,
              pr: 1.5,
              pt: '7px'
            }}
          >
            <IconComponent
              sx={{
                fontSize: 24,
                color: config.main
              }}
            />
          </Box>

          {/* Text Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              py: 1,
              pr: 1,
              justifyContent: 'center'
            }}
          >
            <Box
              component="p"
              sx={{
                fontFamily: '"Public Sans", sans-serif',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px',
                margin: 0,
                color: config.darker
              }}
            >
              {title}
            </Box>
            {message != null && (
              <Box
                component="p"
                sx={{
                  fontFamily: '"Public Sans", sans-serif',
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '22px',
                  margin: 0,
                  color: config.darker
                }}
              >
                {message}
              </Box>
            )}
          </Box>

          {/* Actions and Close Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              pt: 0.5
            }}
          >
            {/* Action Buttons */}
            {actions != null && actions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                {actions.map((action, index) => {
                  const actionStyles = index === 0
                    ? getPrimaryActionStyles(config.main)
                    : getSecondaryActionStyles(config.main)

                  return (
                    <Box
                      key={index}
                      onClick={action.onClick}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        ...actionStyles
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontFamily: '"Public Sans", sans-serif',
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: '22px',
                          color: index === 0 ? '#FFFFFF' : config.main
                        }}
                      >
                        {action.label}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}

            {/* Close Button */}
            <IconButton
              onClick={onClose}
              sx={{
                p: 0.625,
                '&:hover': {
                  bgcolor: 'transparent'
                }
              }}
            >
              <Close
                sx={{
                  fontSize: 18,
                  color: config.darker
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Alert>
    </MuiSnackbar>
  )
}

/**
 * Snackbar（トースト通知）コンポーネント
 *
 * @param open - Snackbarの表示状態
 * @param onClose - Snackbarを閉じる時のコールバック
 * @param severity - 通知の種類（success, error, warning, info）
 * @param title - 通知のタイトル
 * @param message - 通知の詳細メッセージ（オプション）
 * @param actions - アクションボタンの配列（オプション）
 * @param autoHideDuration - 自動で閉じるまでの時間（ミリ秒）。null で自動非表示なし。未指定の場合、errorは自動非表示なし、それ以外は6秒
 * @param anchorOrigin - 表示位置（デフォルト: { vertical: 'top', horizontal: 'right' }）
 *
 * @example
 * ```tsx
 * // 基本的な成功通知
 * <Snackbar
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   severity="success"
 *   title="Success"
 *   message="クリップの作成をリクエストしました"
 * />
 *
 * // アクションボタン付き
 * <Snackbar
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   severity="success"
 *   title="Success"
 *   message="クリップの作成をリクエストしました"
 *   actions={[
 *     { label: 'クリップ一覧を確認', onClick: handleViewClips },
 *     { label: 'キャンセル', onClick: handleCancel }
 *   ]}
 * />
 *
 * // エラー通知（自動で閉じない）
 * <Snackbar
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   severity="error"
 *   title="Error"
 *   message="処理に失敗しました"
 * />
 * ```
 */
export default function Snackbar (props: SnackbarProps) {
  return (
    <CleanThemeProvider>
      <SnackbarContent {...props} />
    </CleanThemeProvider>
  )
}
